import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Page } from '../types/api';
import PublicHeader from '../components/PublicHeader';
import PublicFooter from '../components/PublicFooter';
import DocumentTitle from '../components/DocumentTitle';
import RichTextDisplay from '../components/RichTextDisplay';

const PublicPageView: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPage = async () => {
      if (!slug) return;
      
      try {
        setLoading(true);
        const pages = await apiService.getPublishedPages();
        const foundPage = pages.find(p => p.slug === slug);
        
        if (foundPage) {
          setPage(foundPage);
          setError(null);
        } else {
          setError('Page not found');
        }
      } catch (err) {
        setError('Failed to load page');
        console.error('Error fetching page:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  if (error || !page) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-white">
      <DocumentTitle title={page.title} />
      <PublicHeader />

      {/* Main Content */}
      <main>
        <div className="max-w-4xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          <article className="mx-auto">
            <h1 className="text-4xl font-bold text-gray-900 mb-8">{page.title}</h1>
            <RichTextDisplay 
              content={page.content}
              className="text-gray-700 leading-relaxed"
            />
          </article>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
};

export default PublicPageView;
